import { ReactNode, useMemo } from 'react';
import {
  useCurrentFrame,
  useVideoConfig,
  spring,
  interpolate,
  interpolateColors,
} from 'remotion';

// Themes
import { employeeYearInReviewTheme } from '@/components/EmployeeYearInReview/theme';

// Constants
import {
  ANIMATION_SPRING_CONFIGS,
  CLAMP_BOTH,
  SCENE_3A_3B_OVERLAP_FRAMES,
  SCENE_3B_TEXT_FADE_OUT_DURATION,
} from '@/constants';
import { ANIMATION_CONFIG } from '../constants';

// Types
import { AnimationPreset } from '@/types';
import { Project } from './types';

// Utils
import { getMonthsOffsets, getProjectsOffsets } from '../utils';

const {
  projectsInitial,
  monthsInitial,
  projectsFinal,
  monthsFinalOffsets,
  labelTranslateX,
  labelTranslateY,
} = ANIMATION_CONFIG.projectStatsComponent;

export interface ProjectStatsProps {
  projects: Project[];
  monthsOnProject?: number;
  /** Frame when text should start fading out (relative to this component's frame) */
  textFadeOutStartFrame?: number;
}

export const ProjectStats = ({
  projects,
  monthsOnProject = 0,
  textFadeOutStartFrame,
}: ProjectStatsProps): ReactNode => {
  const { typography, rawColors, fontSize } = employeeYearInReviewTheme;
  const frame = useCurrentFrame();
  const { fps, width, height } = useVideoConfig();

  // Calculate statistics from projects data
  const numberOfProjects = useMemo(() => projects.length, [projects]);
  const totalMonths = monthsOnProject >= 1 ? Math.floor(monthsOnProject) : 1;

  // Animation starts after 1 second (fps frames)
  const moveStartFrame = fps;

  // Text fade out animation before transition
  // If textFadeOutStartFrame is provided, fade out text from 1 to 0 over SCENE_3B_TEXT_FADE_OUT_DURATION
  const contentFadeOutOpacity = textFadeOutStartFrame
    ? interpolate(
        frame,
        [
          textFadeOutStartFrame,
          textFadeOutStartFrame + SCENE_3B_TEXT_FADE_OUT_DURATION,
        ],
        [1, 0],
        CLAMP_BOTH,
      )
    : 1;

  // Movement progress for corner animation (starts after 1 second)
  const moveProgress = spring({
    frame: frame - moveStartFrame,
    fps,
    config: ANIMATION_SPRING_CONFIGS[AnimationPreset.GENTLE],
  });

  const projectsOffsets = getProjectsOffsets(numberOfProjects);
  // "2 projects" position interpolation - moves to top-left
  const projectsTop = interpolate(
    moveProgress,
    [0, 1],
    [projectsInitial.top, projectsFinal.top],
    CLAMP_BOTH,
  );
  const projectsLeft = interpolate(
    moveProgress,
    [0, 1],
    [
      projectsInitial.left + projectsOffsets.initialLeftOffset,
      projectsFinal.left + projectsOffsets.finalLeftOffset,
    ],
    CLAMP_BOTH,
  );

  const monthsFinalTop = height - monthsFinalOffsets.bottom;
  const monthsOffsets = getMonthsOffsets(monthsOnProject);

  const monthsFinalLeft =
    width + monthsFinalOffsets.right + monthsOffsets.finalLeftOffset;

  const monthsTop = interpolate(
    moveProgress,
    [0, 1],
    [monthsInitial.top, monthsFinalTop],
    CLAMP_BOTH,
  );

  const monthsLeft = interpolate(
    moveProgress,
    [0, 1],
    [monthsInitial.left + monthsOffsets.initialLeftOffset, monthsFinalLeft],
    CLAMP_BOTH,
  );

  // Text spacing interpolation (horizontal gap to vertical gap)
  // 0 = horizontal layout with space, 1 = vertical layout (column)
  const layoutProgress = moveProgress;

  // Smooth transition for text label position
  // The label moves from right of number (0) to below number (1)
  // We use transform to create a smooth arc motion

  // Horizontal offset: starts at full width (next to number), ends at 0 (centered below)
  // Adjust labelTranslateX for projects based on number of digits
  // When numberOfProjects >= 10 (2 digits), the number is wider, so we need to adjust the label position
  const labelTranslateXForProjects = interpolate(
    layoutProgress,
    [0, 1],
    [0, labelTranslateX + projectsOffsets.labelTranslateXOffset],
    CLAMP_BOTH,
  );

  const labelTranslateXForMonths = interpolate(
    layoutProgress,
    [0, 1],
    [0, labelTranslateX + monthsOffsets.labelTranslateXOffset],
    CLAMP_BOTH,
  );

  // Vertical offset: starts at 0, moves down below the number
  const labelTranslateYValue = interpolate(
    layoutProgress,
    [0, 1],
    [0, labelTranslateY],
    CLAMP_BOTH,
  );

  // Scale down opacity slightly during transition for smoother feel
  const labelOpacity = interpolate(
    layoutProgress,
    [0, 0.3, 0.7, 1],
    [1, 0.8, 0.8, 1],
    CLAMP_BOTH,
  );

  // Label margin animation (horizontal gap shrinks as layout becomes vertical)
  const labelMarginLeft = interpolate(
    layoutProgress,
    [0, 1],
    [20, 0],
    CLAMP_BOTH,
  );

  // Smooth text color transition from white to frost-900
  // Uses interpolateColors for a smooth animated color change
  const textColor = interpolateColors(
    moveProgress,
    [0, 1],
    [rawColors.textTertiary, rawColors.textTertiary],
  );

  // Font size animation
  // Numbers ("2", "8") grow larger as they move to corners
  const numberFontSize = interpolate(
    moveProgress,
    [0, 1],
    [fontSize.base, fontSize['2xl']], // Starting size to larger size
    CLAMP_BOTH,
  );

  // Labels ("projects", "months") shrink as they move to corners
  const labelFontSize = interpolate(
    moveProgress,
    [0, 1],
    [fontSize.base, fontSize.xs], // Starting size to smaller size
    CLAMP_BOTH,
  );

  // White corner overlay animation progress (starts after Scene 3 text fades out completely)
  const cornerProgress = spring({
    frame: frame - SCENE_3A_3B_OVERLAP_FRAMES,
    fps,
    config: ANIMATION_SPRING_CONFIGS[AnimationPreset.SNAPPY],
  });

  // Top-left corner slide-in (from left)
  const topLeftSlide = interpolate(
    cornerProgress,
    [0, 1],
    [-200, 0],
    CLAMP_BOTH,
  );

  // Bottom-right corner slide-in (from right)
  const bottomRightSlide = interpolate(
    cornerProgress,
    [0, 1],
    [200, 0],
    CLAMP_BOTH,
  );

  const numberStyle = {
    color: textColor,
    fontSize: numberFontSize,
  };

  const labelStyle = {
    color: textColor,
    fontSize: labelFontSize,
    transform: `translate(${labelTranslateXForProjects}%, ${labelTranslateYValue}%)`,
    opacity: labelOpacity,
    marginLeft: labelMarginLeft,
  };

  const monthsLabelStyle = {
    color: textColor,
    fontSize: labelFontSize,
    transform: `translate(${labelTranslateXForMonths}%, ${labelTranslateYValue}%)`,
    opacity: labelOpacity,
    marginLeft: labelMarginLeft,
  };

  return (
    <div className="relative w-full h-full">
      {/* Top-left white diagonal corner */}
      <div
        className="absolute top-0 left-[580px] w-[34%] h-full bg-main-gradient-start origin-top-left -ml-[15%]"
        style={{
          transform: `translateX(${topLeftSlide}%) skewX(-50deg)`,
        }}
      />

      {/* Bottom-right white diagonal corner */}
      <div
        className="absolute bottom-0 right-[580px] w-[34%] h-full bg-main-gradient-end origin-bottom-right -mr-[15%]"
        style={{
          transform: `translateX(${bottomRightSlide}%) skewX(-50deg)`,
        }}
      />

      {/* Number of the projects - Smooth movement to top-left corner */}
      <div
        className="absolute flex flex-row items-center z-10"
        style={{
          top: projectsTop,
          left: projectsLeft,
          opacity: contentFadeOutOpacity,
        }}
      >
        <span className={typography.base} style={numberStyle}>
          {numberOfProjects}
        </span>
        <span className={typography.base} style={labelStyle}>
          {numberOfProjects === 1 ? 'project' : 'projects'}
        </span>
      </div>

      {/* Number of the months - Smooth movement to bottom-right corner */}
      <div
        className="absolute flex flex-row items-center -translate-x-full -translate-y-full"
        style={{
          top: monthsTop,
          left: monthsLeft,
          opacity: contentFadeOutOpacity,
        }}
      >
        <span className={typography.base} style={numberStyle}>
          {totalMonths}
        </span>
        <span className={typography.base} style={monthsLabelStyle}>
          {totalMonths === 1 ? 'month' : 'months'}

          {/* Add space for single digit months to align with double digit months */}
          {totalMonths === 1 && <>&nbsp;</>}
        </span>
      </div>
    </div>
  );
};
